import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { SparkleCircle } from '@lifeomic/chromicons';
import { useHistory, useLocation } from 'react-router-dom';
import type { WithPlatformDefaults } from '@lifeomic/service-clients/fhir-client';

import {
  buildFileValidationTarget,
  fetchFileValidationTargetByDocumentReferenceId,
} from '../../FileValidation/dataAccess';
import { FileValidationResolver } from '../../FileValidation/FileValidationResolver';
import { isCurrentInvalidFileDocumentReference } from '../../FileValidation/fhir';
import {
  FILE_VALIDATION_DOCUMENT_REFERENCE_ID_PARAM,
  type FileValidationTarget,
} from '../../FileValidation/types';
import { formatMember } from '../../../workflows/utils';

const messages = defineMessages({
  resolveFileValidationAlert: {
    id: 'MemberDocuments.resolveFileValidationAlert',
    defaultMessage: 'Resolve file validation alert',
  },
  fileValidationLoadingTitle: {
    id: 'MemberDocuments.fileValidationLoadingTitle',
    defaultMessage: 'Loading file validation alert',
  },
  fileValidationLoadingBody: {
    id: 'MemberDocuments.fileValidationLoadingBody',
    defaultMessage: 'Loading the selected file validation alert…',
  },
  fileValidationNotFoundTitle: {
    id: 'MemberDocuments.fileValidationNotFoundTitle',
    defaultMessage: 'File validation alert not found',
  },
  fileValidationNotFoundBody: {
    id: 'MemberDocuments.fileValidationNotFoundBody',
    defaultMessage:
      'The selected file validation alert could not be found for this member.',
  },
  fileValidationErrorTitle: {
    id: 'MemberDocuments.fileValidationErrorTitle',
    defaultMessage: 'Unable to load file validation alert',
  },
  fileValidationErrorBody: {
    id: 'MemberDocuments.fileValidationErrorBody',
    defaultMessage:
      'Try refreshing this page. If the alert still does not load, contact #tech-support.',
  },
  close: {
    id: 'MemberDocuments.close',
    defaultMessage: 'Close',
  },
});

export const toPlatformPatient = (
  member: fhir.Patient,
): WithPlatformDefaults<fhir3.Patient> =>
  ({
    ...member,
    meta: {
      tag: [],
      lastUpdated: '',
      ...member.meta,
    },
    extension: member.extension ?? [],
    id: member.id ?? '',
  } as WithPlatformDefaults<fhir3.Patient>);

export const useMemberFileValidationResolution = ({
  member,
  memberId,
  canResolveVisibleEntryPoint,
  onResolutionConfirmed,
}: {
  member: fhir.Patient | null;
  memberId: string;
  canResolveVisibleEntryPoint: boolean;
  onResolutionConfirmed: () => void;
}) => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const location = useLocation();
  const [selectedRowTarget, setSelectedRowTarget] =
    useState<FileValidationTarget | null>(null);
  const previousSelectedDocumentReferenceId = React.useRef<string | null>(null);

  const platformPatient = useMemo(
    () => (member ? toPlatformPatient(member) : null),
    [member],
  );
  const selectedDocumentReferenceId = new URLSearchParams(location.search).get(
    FILE_VALIDATION_DOCUMENT_REFERENCE_ID_PARAM,
  );
  const selectedLocalTarget =
    selectedRowTarget &&
    (!selectedDocumentReferenceId ||
      selectedRowTarget.documentReferenceId === selectedDocumentReferenceId)
      ? selectedRowTarget
      : null;

  React.useEffect(() => {
    if (
      previousSelectedDocumentReferenceId.current &&
      !selectedDocumentReferenceId
    ) {
      setSelectedRowTarget(null);
    }

    previousSelectedDocumentReferenceId.current = selectedDocumentReferenceId;
  }, [selectedDocumentReferenceId]);

  const setSelectedDocumentReferenceId = useCallback(
    (documentReferenceId: string) => {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set(
        FILE_VALIDATION_DOCUMENT_REFERENCE_ID_PARAM,
        documentReferenceId,
      );
      history.push({
        ...location,
        search: `?${searchParams.toString()}`,
      });
    },
    [history, location],
  );

  const clearSelectedDocumentReferenceId = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete(FILE_VALIDATION_DOCUMENT_REFERENCE_ID_PARAM);
    history.replace({
      ...location,
      search: searchParams.toString() ? `?${searchParams.toString()}` : '',
    });
  }, [history, location]);

  const directTargetQuery = useQuery({
    queryKey: [
      'member-documents-file-validation-target',
      memberId,
      selectedDocumentReferenceId,
    ],
    enabled: Boolean(
      memberId &&
        platformPatient &&
        selectedDocumentReferenceId &&
        !selectedLocalTarget,
    ),
    queryFn: () =>
      fetchFileValidationTargetByDocumentReferenceId({
        documentReferenceId: selectedDocumentReferenceId || '',
        expectedPatientId: memberId,
        patient: platformPatient || undefined,
        providers: [],
        formatMessage,
      }),
  });

  const openDocumentReference = useCallback(
    (documentReference: WithPlatformDefaults<fhir3.DocumentReference>) => {
      if (!platformPatient) {
        return;
      }

      const nextTarget = buildFileValidationTarget({
        patient: platformPatient,
        documentReference,
        member: formatMember(platformPatient, {
          formatMessage,
          providers: [],
        }),
      });

      if (nextTarget) {
        setSelectedDocumentReferenceId(nextTarget.documentReferenceId);
        setSelectedRowTarget(nextTarget);
      }
    },
    [formatMessage, platformPatient, setSelectedDocumentReferenceId],
  );

  const renderRowAction = useCallback(
    (documentReference: fhir.DocumentReference | null) => {
      if (
        !canResolveVisibleEntryPoint ||
        !documentReference?.id ||
        !platformPatient ||
        !isCurrentInvalidFileDocumentReference(
          documentReference as WithPlatformDefaults<fhir3.DocumentReference>,
        )
      ) {
        return null;
      }

      return (
        <Tooltip title={formatMessage(messages.resolveFileValidationAlert)}>
          <IconButton
            icon={SparkleCircle}
            size={0}
            color="inverse"
            aria-label={formatMessage(messages.resolveFileValidationAlert)}
            onClick={event => {
              event.stopPropagation();
              event.currentTarget.blur();
              openDocumentReference(
                documentReference as WithPlatformDefaults<fhir3.DocumentReference>,
              );
            }}
          />
        </Tooltip>
      );
    },
    [
      canResolveVisibleEntryPoint,
      formatMessage,
      openDocumentReference,
      platformPatient,
    ],
  );

  const lookupFailed =
    selectedDocumentReferenceId &&
    !selectedLocalTarget &&
    directTargetQuery.data &&
    directTargetQuery.data.status !== 'success' &&
    directTargetQuery.data.status !== 'error';
  const lookupError =
    selectedDocumentReferenceId &&
    !selectedLocalTarget &&
    (directTargetQuery.isError || directTargetQuery.data?.status === 'error');
  const directTarget =
    directTargetQuery.data?.status === 'success'
      ? directTargetQuery.data.target
      : null;
  const activeTarget = selectedLocalTarget || directTarget || null;

  const content = (
    <>
      <FileValidationResolver
        isOpen={!!activeTarget}
        target={activeTarget}
        onDismiss={() => {
          setSelectedRowTarget(null);
          if (selectedDocumentReferenceId) {
            clearSelectedDocumentReferenceId();
          }
        }}
        onResolutionConfirmed={() => {
          setSelectedRowTarget(null);
          if (selectedDocumentReferenceId) {
            clearSelectedDocumentReferenceId();
          }
          onResolutionConfirmed();
        }}
      />
      <Modal
        isOpen={
          !!selectedDocumentReferenceId &&
          !selectedLocalTarget &&
          directTargetQuery.isLoading
        }
        onDismiss={clearSelectedDocumentReferenceId}
        title={formatMessage(messages.fileValidationLoadingTitle)}
      >
        <Text>{formatMessage(messages.fileValidationLoadingBody)}</Text>
      </Modal>
      <Modal
        isOpen={!!(lookupFailed || lookupError)}
        onDismiss={clearSelectedDocumentReferenceId}
        title={formatMessage(
          lookupFailed
            ? messages.fileValidationNotFoundTitle
            : messages.fileValidationErrorTitle,
        )}
        actions={
          <Button variant="text" onClick={clearSelectedDocumentReferenceId}>
            {formatMessage(messages.close)}
          </Button>
        }
      >
        <Text>
          {formatMessage(
            lookupFailed
              ? messages.fileValidationNotFoundBody
              : messages.fileValidationErrorBody,
          )}
        </Text>
      </Modal>
    </>
  );

  return {
    content,
    renderRowAction,
  };
};
